import { DictType, ProjectFile } from "@pma/types"
import { useMutation, useQuery } from "@tanstack/react-query"
import { toast } from "@hooks/use-toast"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import { useContext, useState } from "react"
import useTranslator from "@hooks/use-translator"
import {
  ADD_FILES_TO_NOTE_ENDPOINT,
  ADD_FILES_TO_TASK_ENDPOINT,
  DIGITAL_OCEAN_FILES_ENDPOINT,
  GET_FILES_ENDPOINT,
  SERVER_URL,
} from "@pma/utils/api-routes"
import axios from "axios"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN, queryClient } from "@pma/utils/constants"
import { RenderFileIcon } from "../files/files-view"
import AddFileToNote from "../notifications/add-file-to-note"
import { FilePreview } from "../dashboard/preview-file"
import { cn } from "@pma/utils/helpers"
import DeleteFile from "../dashboard/delete-file"
import { Popover, PopoverContent, PopoverTrigger } from "@components/common/popover"
import DownloadFile from "../files/download-file"
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@components/common/hover-card"
import { ScrollArea } from "../common/scroll-area"
import PrintFile from "../files/print-file"
import ShareFileOnWhatsApp from "../files/share-file-on-whatsapp"
import { GroupedTasksContext } from "./grouped-tasks/context/GroupedTasksProvider"
import { Mail } from "lucide-react"
import { Button } from "../common/button"
import ShareFilesFoldersViaEmail from "../files/share-files-folders-via-email"

interface Props {
  taskId: number
  files: Array<DictType>
  size?: "lg" | "sm"
  notes?: boolean
  showAsFolder?: boolean
  parentTaskId?: number
}
const AttachFileToTask = ({
  taskId: id,
  files,
  size,
  notes,
  showAsFolder = true,
  parentTaskId,
}: Props) => {
  const [searchParam] = useSearchParams()
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const newParams = new URLSearchParams(searchParam)
  const { projectId } = useParams()

  const { setTaskExpandForLoading } = useContext(GroupedTasksContext)

  const [file, setFile] = useState<[any] | []>([])
  const [resetState, setResetState] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<DictType[]>([])
  const [shareViaEmailModal, setShareViaEmailModal] = useState<boolean>(false)

  const navigate = useNavigate()
  const { translate } = useTranslator()

  const addToFileTask = async (files: ProjectFile[]) => {
    const file_ids = files?.map((f) => {
      return f.id
    })
    const response = await axios.post(
      SERVER_URL + ADD_FILES_TO_TASK_ENDPOINT,
      {
        task_id: id,
        inform_users: true,
        files_id: file_ids,
      },
      {
        headers: {
          Authorization: `Bearer ${parseCookies()[ACCESS_TOKEN]}`,
        },
      },
    )

    return response.data
  }

  const addFileToTaskMutation = useMutation({
    mutationKey: ["add-file-to-task", id],
    mutationFn: addToFileTask,
    onSuccess: (data) => {
      //Trigger Revalidate tasks
      newParams.set("revalidate", "tasks")
      navigate(`?${newParams.toString()}`)
      queryClient.invalidateQueries({ queryKey: ["grouped-tasks"] })
      if (parentTaskId) {
        setTaskExpandForLoading(parentTaskId, { toggleExpanded: () => {} } as any)
      }
      setResetState(true)
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message

      toast({
        title: translate("Error"),
        description: translate(errorMessage || "Error occurred"),
        variant: "error",
      })
    },
  })

  const addToFileNote = async (files: any[]) => {
    const file_ids = files?.map((f) => {
      return f.id
    })
    const response = await axios.post(
      SERVER_URL + ADD_FILES_TO_NOTE_ENDPOINT,
      {
        note_id: id,
        inform_users: true,
        files_id: file_ids,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const addFileToNoteMutation = useMutation({
    mutationKey: ["add-file-to-note", id],
    mutationFn: addToFileNote,
    onSuccess: (data) => {
      // Reset file
      setFile([])
      //Trigger Revalidate project/tasks
      newParams.set("revalidate", "tasks")
      navigate(`?${newParams.toString()}`)
      setResetState(true)
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message

      toast({
        title: translate("Error"),
        description: translate(errorMessage || "Error occurred"),
        variant: "error",
      })
    },
  })

  const uploadFile = async () => {
    const formData = new FormData()

    if (file.length == 0) {
      return
    }

    if (file?.length > 0) {
      ;[...file]?.forEach((val) => {
        formData.append("files", val)
      })
    }

    const url = `${DIGITAL_OCEAN_FILES_ENDPOINT}?token=${accessToken}`

    const response = await axios.post(url, formData, {
      headers: {
        accept: "*/*",
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent: any) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
      },
    })

    return response?.data
  }

  const uploadFileMutation = useMutation({
    mutationKey: ["upload-file"],
    mutationFn: uploadFile,
    onSuccess: (data) => {
      const payload = data as ProjectFile[]

      toast({
        title: translate("Success"),
        description: translate("File uploaded successfully"),
        variant: "success",
      })

      if (notes)
        addFileToNoteMutation.mutate(payload) // Link digital ocean file to note
      else addFileToTaskMutation.mutate(payload) // Link digital ocean file to  task
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: errorMessage || translate("Unable to upload file at this time"),
        variant: "error",
      })
    },
  })

  return (
    <div
      className={cn("z-10 flex items-start gap-1 p-1", true ? "w-full items-center" : "w-[100px]")}
    >
      {showAsFolder ? (
        <Popover>
          <PopoverTrigger className='relative z-10'>
            {files?.length ? (
              <>
                <small className='absolute -right-1 -top-2.5 flex h-3 w-3 items-center justify-center rounded-full bg-slate-300 text-[10px] text-primary shadow-md'>
                  {files?.length}
                </small>
                <RenderFileIcon type={"file"} size={size} />
              </>
            ) : (
              <></>
            )}
          </PopoverTrigger>
          <PopoverContent className='z-50 grid w-[350px] flex-1'>
            {files?.length > 0 ? (
              <ScrollArea
                className={cn(
                  "h-[350px] w-full flex-1 gap-4",
                  files?.length > 3 ? "h-[350px]" : "h-[120px]",
                )}
              >
                <div className='grid h-full w-full grid-cols-3 gap-1 p-2'>
                  {files?.map((file: any, index: number) => (
                    <HoverCard key={file?.id}>
                      <HoverCardTrigger className='flex w-20 flex-col items-center gap-4'>
                        <TaskFilesPreview fileId={file?.id} size={"lg"} />
                        <span className='max-w-[90px] overflow-hidden truncate text-ellipsis text-center text-xs'>
                          {file?.name && (
                            <span>
                              {file?.name?.slice(0, 5)}...{file.type}
                            </span>
                          )}
                        </span>
                      </HoverCardTrigger>

                      <HoverCardContent className='flex w-fit items-center gap-2'>
                        <DownloadFile file={file} />
                        <PrintFile file={file} />
                        <ShareFileOnWhatsApp file={file} />
                        <Tooltip>
                          <TooltipTrigger>
                            <Button
                              size={"icon"}
                              className='h-6 w-7'
                              variant={"ghost"}
                              aria-label={translate("Share on Mail")}
                              onClick={() => {
                                setSelectedFiles([file])
                                setShareViaEmailModal(true)
                              }}
                            >
                              <Mail size={14} />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>{translate("Share on Mail")}</TooltipContent>
                        </Tooltip>
                        <DeleteFile file={file} />
                      </HoverCardContent>
                    </HoverCard>
                  ))}
                </div>
              </ScrollArea>
            ) : (
              <div className='col-span-3'>{translate("Folder is empty")}</div>
            )}
          </PopoverContent>
        </Popover>
      ) : (
        <div className='z-50 grid flex-1 grid-cols-3 gap-2'>
          {files?.length > 0 ? (
            <>
              {files?.map((file: any, index: number) => (
                <div className='z-10 flex flex-row flex-wrap p-2' key={index}>
                  <div className='flex w-[100%] justify-end'>
                    <DeleteFile file={file} />
                    <DownloadFile file={file} />
                  </div>

                  <div className='flex flex-col'>
                    <TaskFilesPreview fileId={file?.id} size={"lg"} />
                    <span className='max-w-[90px] overflow-hidden truncate text-ellipsis text-center text-xs'>
                      {file?.name && (
                        <span>
                          {file?.name?.slice(0, 5)}...{file.type}
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className='col-span-3'>{translate("Folder is empty")}</div>
          )}
        </div>
      )}

      <AddFileToNote
        context='task'
        setFile={setFile}
        reset={showAsFolder ? true : !file}
        uploadFunction={uploadFileMutation.mutate}
        resetState={resetState}
        setResetState={setResetState}
      />

      {shareViaEmailModal && (
        <ShareFilesFoldersViaEmail
          modalOpen={shareViaEmailModal}
          setModalOpen={setShareViaEmailModal}
          selectedFiles={selectedFiles}
          project={{ id: projectId }}
        />
      )}
    </div>
  )
}

export default AttachFileToTask

const TaskFilesPreview = ({ fileId, size }: { fileId: number; size: "sm" | "lg" }) => {
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const getFileById = async () => {
    const response = await axios.get(SERVER_URL + `${GET_FILES_ENDPOINT}${fileId}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })

    return response.data
  }

  const { data: fileContent } = useQuery({
    queryKey: ["file-content", fileId],
    queryFn: getFileById,
  })

  return (
    <FilePreview file={fileContent}>
      <Tooltip>
        <TooltipTrigger>
          <RenderFileIcon type={fileContent?.type} size={size} />
        </TooltipTrigger>
        <TooltipContent>{fileContent?.name}</TooltipContent>
      </Tooltip>
    </FilePreview>
  )
}
